import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { authService } from '@/services/auth/authService';
import { useAuth } from '@/contexts/AuthContext';
import { authLogger } from '@/utils/logger';

/**
 * Landing route for the authorization-code callback (`/auth/callback`), shared by the
 * silent `prompt=none` round and the interactive login (D13b).
 *
 * On success the token family is in memory — we resume via a CLIENT-SIDE navigation so
 * the in-memory tokens survive (a full reload would wipe them). On `login_required`
 * (silent round, no live auth-host cookie) we fall back to the interactive login page,
 * never a redirect loop.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const { refreshAuthState } = useAuth();
  const handled = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    (async () => {
      try {
        const returnUrl = await authService.completeSignin();
        refreshAuthState();
        navigate(returnUrl, { replace: true });
      } catch (err) {
        if (authService.isInteractionRequired(err)) {
          // Silent round could not complete without user interaction. The automatic
          // attempt is spent; `interaction=required` tells the login screen to surface
          // an EXPLICIT sign-in action rather than starting another authorize round on
          // its own (D18 item 36). That matters most on a tenant subdomain reached
          // while an auth-host session for a different tenant is still live: an
          // automatic round there could complete off the live Entra session and switch
          // the user's tenant without them ever acting.
          navigate('/login?interaction=required', { replace: true });
          return;
        }
        authLogger.error('Auth callback failed', err instanceof Error ? err : undefined);
        setError('Sign-in could not be completed. Please try again.');
      }
    })();
  }, [navigate, refreshAuthState]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      {error ? (
        <>
          <Typography color="error">{error}</Typography>
          <Typography
            component="button"
            onClick={() => navigate('/login', { replace: true })}
            sx={{ cursor: 'pointer', background: 'none', border: 'none', textDecoration: 'underline' }}
          >
            Back to sign in
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography color="text.secondary">Signing you in…</Typography>
        </>
      )}
    </Box>
  );
}
